import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Flame, Utensils, Leaf, MapPin, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { CTAButton } from "@/components/CTAButton";
import QuickActions from "@/components/QuickActions";
import EditorialMarquee from "@/components/EditorialMarquee";
import { Reveal, LineReveal, Stagger, StaggerItem } from "@/components/Reveal";
import GalleryGrid from "@/components/GalleryGrid";
import FaqAccordion from "@/components/FaqAccordion";
import ReviewCard from "@/components/ReviewCard";
import { BUSINESS, WHY_US, CATEGORIES, HOW_IT_WORKS, REVIEWS } from "@/data/site";

const ICONS = { Flame, Utensils, Leaf, MapPin };
const HERO_IMG =
  "https://images.pexels.com/photos/12455439/pexels-photo-12455439.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";

function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.15]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[620px] overflow-hidden bg-brand-ink" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Close-up of a steaming noodle bowl with chopsticks"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/40 to-brand-ink/30" />
      </motion.div>

      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-5 md:px-8 pb-16 md:pb-20">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="text-xs md:text-sm uppercase tracking-[0.28em] text-brand-gold-soft font-medium"
        >
          Sector xxx · Gurugram
        </motion.span>
        <h1 className="mt-4 font-display text-white text-[15vw] sm:text-7xl md:text-8xl lg:text-[8.5vw] leading-[0.9] tracking-tight">
          <LineReveal lines={["Wok-fired.", "Hand-pulled.", "Unforgettable."]} startDelay={0.15} />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-6 max-w-xl text-lg md:text-xl text-white/80 leading-relaxed"
        >
          {BUSINESS.tagline} A warm, contemporary corner for dim sum, noodles and
          the wok classics you crave.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-8 flex flex-wrap gap-3"
        >
          <CTAButton href={`tel:${BUSINESS.phoneHref}`} variant="primary" icon={false}>
            Call to book a table
          </CTAButton>
          <CTAButton to="/menu" variant="outlineLight">
            Explore the menu
          </CTAButton>
        </motion.div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title="Modern Chinese Dining in Sector xxx"
        description="Chopstick Chinese Corner serves wok-fired classics, hand-pulled noodles and dim sum in Sector xxx, Gurugram. Call, get directions or view the menu."
      />
      <Hero />

      {/* Quick actions */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 -mt-10 relative z-20">
        <Reveal>
          <QuickActions tone="dark" />
        </Reveal>
      </section>

      {/* Why choose us */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 py-24 md:py-32" data-testid="why-us">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.24em] text-brand-red font-medium">Why Chopstick</span>
              <h2 className="mt-4 font-display text-4xl md:text-5xl leading-tight text-brand-ink">
                Small corner.
                <br />
                Serious flavour.
              </h2>
              <p className="mt-5 text-muted-foreground leading-relaxed max-w-sm">
                We keep it honest: fresh ingredients, high heat and recipes cooked
                to order — the way great Chinese food should be.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Stagger className="grid gap-px bg-brand-ink/10 sm:grid-cols-2 border border-brand-ink/10">
              {WHY_US.map((w) => {
                const Icon = ICONS[w.icon];
                return (
                  <StaggerItem key={w.title}>
                    <div className="group h-full bg-brand-cream p-8 transition-colors duration-300 hover:bg-white">
                      <Icon className="text-brand-red" size={26} />
                      <h3 className="mt-5 font-display text-2xl text-brand-ink">{w.title}</h3>
                      <p className="mt-3 text-muted-foreground leading-relaxed">{w.body}</p>
                    </div>
                  </StaggerItem>
                );
              })}
            </Stagger>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* Featured menu categories — bento */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 py-24 md:py-32" data-testid="featured-categories">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.24em] text-brand-red font-medium">The menu</span>
            <h2 className="mt-4 font-display text-4xl md:text-6xl leading-none text-brand-ink">Featured categories</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <CTAButton to="/menu" variant="ghost">See full menu</CTAButton>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.08} className={c.span}>
              <Link
                to="/menu"
                className="group relative block h-72 md:h-80 overflow-hidden bg-brand-ink"
                data-testid={`category-${c.id}`}
              >
                <img
                  src={c.image}
                  alt={c.name}
                  loading="lazy"
                  className="h-full w-full object-cover opacity-90 transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/85 via-brand-ink/20 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-7">
                  <h3 className="font-display text-3xl md:text-4xl text-white">{c.name}</h3>
                  <p className="mt-2 max-w-md text-white/75 text-sm leading-relaxed">{c.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-brand-gold-soft text-sm font-medium">
                    Explore <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* About strip */}
      <section className="bg-brand-ink text-white py-24 md:py-32" data-testid="home-about">
        <div className="mx-auto max-w-7xl px-5 md:px-8 grid gap-14 lg:grid-cols-2 items-center">
          <Reveal>
            <div className="relative overflow-hidden">
              <img
                src="https://images.pexels.com/photos/10135116/pexels-photo-10135116.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1000"
                alt="Warm, contemporary restaurant interior"
                loading="lazy"
                className="w-full h-[420px] object-cover"
              />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <span className="text-xs uppercase tracking-[0.24em] text-brand-gold-soft font-medium">Our story</span>
              <h2 className="mt-4 font-display text-4xl md:text-5xl leading-tight">
                A neighbourhood table for modern Chinese.
              </h2>
              <p className="mt-6 text-white/75 leading-relaxed text-lg">
                Chopstick Chinese Corner is built around one idea — food that feels
                both comforting and considered. We bring together fresh produce,
                authentic sauces and a warm, contemporary space in Sector xxx.
              </p>
              <p className="mt-4 text-white/60 leading-relaxed">
                Whether it&apos;s a quick weekday lunch or a long dinner with friends,
                we want this corner to feel like your corner.
              </p>
              <CTAButton to="/about" variant="gold" className="mt-8">Read our story</CTAButton>
            </Reveal>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 py-24 md:py-32" data-testid="how-it-works">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.24em] text-brand-red font-medium">How it works</span>
          <h2 className="mt-4 font-display text-4xl md:text-6xl leading-none text-brand-ink">Three easy steps</h2>
        </Reveal>
        <div className="mt-14 grid gap-px bg-brand-ink/10 md:grid-cols-3 border border-brand-ink/10">
          {HOW_IT_WORKS.map((s, i) => (
            <Reveal key={s.step} delay={i * 0.1}>
              <div className="h-full bg-brand-cream p-8 md:p-10">
                <span className="font-display text-6xl md:text-7xl text-brand-red/20">{s.step}</span>
                <h3 className="mt-4 font-display text-2xl text-brand-ink">{s.title}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Gallery preview */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 pb-24 md:pb-32" data-testid="home-gallery">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.24em] text-brand-red font-medium">The vibe</span>
            <h2 className="mt-4 font-display text-4xl md:text-6xl leading-none text-brand-ink">On the table</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <CTAButton to="/gallery" variant="ghost">Full gallery</CTAButton>
          </Reveal>
        </div>
        <Reveal>
          <GalleryGrid limit={10} />
        </Reveal>
      </section>

      {/* Review highlights */}
      <section className="bg-white border-y border-brand-ink/10 py-24 md:py-32" data-testid="home-reviews">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.24em] text-brand-red font-medium">Kind words</span>
            <h2 className="mt-4 font-display text-4xl md:text-6xl leading-none text-brand-ink">Review highlights</h2>
            <p className="mt-4 text-sm text-muted-foreground">Sample testimonials shown for layout — real reviews will appear here.</p>
          </Reveal>
          <Stagger className="mt-12 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            {REVIEWS.map((r, i) => (
              <StaggerItem key={i}><ReviewCard review={r} index={i} /></StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Location */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 py-24 md:py-32" data-testid="home-location">
        <div className="grid gap-12 lg:grid-cols-2 items-center">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.24em] text-brand-red font-medium">Find us</span>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-tight text-brand-ink">
              Right around the corner in Sector xxx.
            </h2>
            <address className="not-italic mt-6 text-lg text-muted-foreground leading-relaxed">
              {BUSINESS.addressLine1}
              <br />
              {BUSINESS.addressLine2}, {BUSINESS.addressRegion}
            </address>
            <p className="mt-3 text-brand-ink font-medium">{BUSINESS.hoursShort}</p>
            <div className="mt-8"><QuickActions tone="dark" /></div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden border border-brand-ink/12">
              <iframe
                title="Map to Chopstick Chinese Corner, Sector xxx Gurugram"
                src={BUSINESS.mapEmbed}
                className="w-full h-[420px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-map"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-5 md:px-8 pb-24 md:pb-32" data-testid="home-faq">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.24em] text-brand-red font-medium">Good to know</span>
          <h2 className="mt-4 font-display text-4xl md:text-6xl leading-none text-brand-ink mb-10">Questions</h2>
        </Reveal>
        <Reveal>
          <FaqAccordion />
        </Reveal>
      </section>

      {/* Final CTA */}
      <section className="bg-brand-red text-white py-24 md:py-32" data-testid="final-cta">
        <div className="mx-auto max-w-4xl px-5 md:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95]">
              Hungry yet?
            </h2>
            <p className="mt-6 text-white/85 text-lg max-w-xl mx-auto">
              Book a table, get directions, or order in. Your next favourite meal
              is a wok away.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <CTAButton href={`tel:${BUSINESS.phoneHref}`} variant="gold" icon={false}>
                Call {BUSINESS.phoneDisplay}
              </CTAButton>
              <CTAButton href={BUSINESS.directionsUrl} variant="outlineLight" target="_blank" rel="noopener noreferrer">
                Get directions
              </CTAButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
